import { hashStringToInteger, mod } from "@/utils/math-utils";
import tailwindConfig from "tailwind.config";
import resolveConfig from "tailwindcss/resolveConfig";
import { TailwindColorGroup, TailwindThemeColors } from "tailwindcss/tailwind-config";
import { RequiredConfig } from "tailwindcss/types/config";

const config = resolveConfig(tailwindConfig as unknown as RequiredConfig);

export function getTailwindConfig(): any {
  return config;
}

const BLUE_DEPLOYMENT_REGEX = /^blue \(\w+\)$/;
const GREEN_DEPLOYMENT_REGEX = /^green \(\w+\)$/;

const SEMANTIC_POSITIVE_WORDS = new Set(["on", "success", "yes", "positive"]);
const SEMANTIC_NEGATIVE_WORDS = new Set(["off", "failure", "no", "negative", "errors"]);

export const getColorOverrideForSeries = (
  label: string | HTMLElement | undefined,
  colorOverrides: Record<string, string> | undefined,
): string | undefined => {
  if (typeof label !== "string") {
    return;
  }
  const labelLowerCase = label.toLowerCase().trim();
  if (colorOverrides) {
    if (labelLowerCase in colorOverrides) {
      return colorOverrides[labelLowerCase];
    } else if (label in colorOverrides) {
      return colorOverrides[label];
    }
  }
  if (SEMANTIC_POSITIVE_WORDS.has(labelLowerCase)) {
    return "#69B190";
  } else if (SEMANTIC_NEGATIVE_WORDS.has(labelLowerCase)) {
    return "#DF4828";
  } else if (BLUE_DEPLOYMENT_REGEX.test(labelLowerCase)) {
    return "#2563eb";
  } else if (GREEN_DEPLOYMENT_REGEX.test(labelLowerCase)) {
    return "#16a34a";
  }
};

// https://medium.com/carbondesign/color-palettes-and-accessibility-features-for-data-visualization-7869f4874fca
export const getCarbonColorPaletteLight = () => {
  return [
    "#1192e9",
    "#6929c5",
    "#efc328", // borrowed this one from datadog
    "#005d5d",
    "#9f1853",
    "#fa4e56",
    "#520408",
    "#197f38",
    "#002d9d",
    "#ee5398",
    "#b28500",
    "#009d9a",
    "#01274a",
    "#8a3800",
    "#a66eff",
  ];
};

/**
 * For dark backgrounds
 */
export const getCarbonColorPaletteDark = () => {
  return [
    "#33b1ff",
    "#893ffc",
    "#efc328", // borrowed this one from datadog
    "#007d7a",
    "#ff7eb5",
    "#fa4e56",
    "#fff2f2",
    "#6fdc8c",
    "#4689ff",
    "#d02770",
    "#d3a107",
    "#09bdb9",
    "#bbe6fe",
    "#ba4e00",
    "#d4bcff",
  ];
};

export const getNiceColorPaletteForSeries = () => {
  return [
    // "#E8ECFB", //
    // "#DDD8EF", //
    "#5568B8", //
    "#60AB9E", //
    "#DDAA3C", //
    //
    "#A778B4", //
    "#9B62A7", //
    "#4E96BC", //
    "#77B77D", //
    "#E49C39", //
    //
    "#8C4E99", //
    "#549EB3", //
    "#A6BE54", //
    "#E78C35", //
    //
    "#C3A8D1", //
    "#4E79C5", //
    "#8CBC68", //
    "#E67932", //
    //
    "#D1C1E1", //
    "#6059A9", //
    "#59A5A9", //
    "#BEBC48", //
    "#E4632D", //
    //
    "#B58FC2", //
    "#6F4C9B", //
    "#DF4828", //
    "#69B190", //
    "#D1B541", //
    "#4D8AC6", //
    // "#DA2222", //
    // "#B8221E", //
    // "#95211B", //
    // "#721E17", //
    // "#521A13", //
  ].reverse();
};

export const getNiceColors = () => {
  return [
    // "#33514a",
    "#7a8a87",
    "#87cc9b",
    "#c95736",
    "#ee9e86",
    "#b787f5",
    "#bcda6e",
    "#95c7cb",
    "#477167",
    "#939f9d",
    "#a9dab7",
    "#d4775e",
    "#f5c6b8",
    "#d2b4f9",
    "#cae28d",
    "#bbdbdd",
    "#273f39",
    "#697775",
    "#70c287",
    "#b14b2f",
    "#eb8d70",
    "#a468f3",
    "#abd14d",
    "#92c5c8",
    "#172622",
    "#566160",
    "#54b66f",
    "#913e27",
    "#e6704c",
    "#8d42f0",
    "#9ac332",
    "#77b7bb",
  ];
};

export interface ColorConfig {
  name: string;
  category: string;
  darkness: number;
  color: string;
  colorGroup: string;
}

const colorRegex = /^#[0-9A-F]{3,6}$/i;

const recursivelyCollectColors = (
  category: string,
  colors: Record<string, TailwindColorGroup | string> | undefined,
  prefix: string = "",
): ColorConfig[] => {
  if (!colors) {
    return [];
  }

  return Object.entries(colors)
    .map(([colorKey, value]): ColorConfig[] => {
      if (typeof value === "string" && colorRegex.test(value)) {
        return [
          {
            name: prefix ? `${prefix}-${colorKey}` : colorKey,
            category,
            darkness: isNaN(Number(colorKey)) ? -1 : Number(colorKey),
            color: value,
            colorGroup: prefix || colorKey,
          },
        ];
      }

      if (typeof value === "object") {
        return recursivelyCollectColors(
          category,
          value,
          prefix ? `${prefix}-${colorKey}` : colorKey,
        );
      }

      return [];
    })
    .flat();
};

const flattenColors = (
  category: string,
  originalPrefix: string,
  colors: Record<string, TailwindColorGroup> | undefined,
): ColorConfig[] => {
  if (!colors) {
    return [];
  }

  return recursivelyCollectColors(category, colors, originalPrefix);
};

export function getTailwindColors() {
  const themeColors: TailwindThemeColors = getTailwindConfig().theme.colors ?? {};
  const { cds: cdsColors, marketing: marketingColors, ...standardColors } = themeColors as any;

  return [
    ...flattenColors("standard", "", standardColors as Record<string, TailwindColorGroup>),
    ...flattenColors("marketing", "marketing", marketingColors),
    ...flattenColors("design system", "cds", cdsColors),
  ];
}

let _TAILWIND_COLORS_BY_NAME: Map<string, ColorConfig> | null = null;

export const getTailwindColorsNameToHex = () => {
  if (_TAILWIND_COLORS_BY_NAME == null) {
    _TAILWIND_COLORS_BY_NAME = new Map(getTailwindColors().map((color) => [color.name, color]));
  }
  return _TAILWIND_COLORS_BY_NAME;
};

export const getTailwindColorHexByName = (name: string): string | null => {
  return getTailwindColorsNameToHex().get(name)?.color ?? null;
};

export const getRequiredTailwindColorHexByName = (name: string): string => {
  const color = getTailwindColorHexByName(name);
  if (color == null) {
    throw new Error(`Unknown Tailwind color: ${name}`);
  }
  return color;
};

export function chooseByString<T>(values: T[], s: string): T {
  const index = hashStringToInteger(s);
  return values[mod(index, values.length)];
}

export const getTagColors = () =>
  getTailwindColors()
    .filter((color) => 400 <= color.darkness && color.darkness <= 800)
    .map((c) => c.color);

export function tagToColor(envName: string): string {
  switch (envName.toLowerCase()) {
    case "all":
      return "#468f85";
    default:
      return chooseByString(getNiceColors(), envName);
  }
}

export function shadeHexColor(color: string, percent: number) {
  const f = parseInt(color.slice(1), 16),
    t = percent < 0 ? 0 : 255,
    p = percent < 0 ? percent * -1 : percent,
    R = f >> 16,
    G = (f >> 8) & 0x00ff,
    B = f & 0x0000ff;

  return (
    "#" +
    (
      0x1000000 +
      (Math.round((t - R) * p) + R) * 0x10000 +
      (Math.round((t - G) * p) + G) * 0x100 +
      (Math.round((t - B) * p) + B)
    )
      .toString(16)
      .slice(1)
  );
}

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const SHORT_HEX_COLOR_PATTERN = /^#[0-9a-f]{3}$/i;

/** Expands `#rgb` to `#rrggbb`. Anything else passes through for the caller to validate. */
function expandShortHex(color: string): string {
  return SHORT_HEX_COLOR_PATTERN.test(color)
    ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
    : color;
}

/**
 * Converts a `#rgb`/`#rrggbb` color into an `rgba(r, g, b, alpha)` string. Non-hex inputs (named
 * colors, rgb()/rgba() strings) are returned unchanged, since we cannot re-alpha them textually.
 */
export function hexColorWithAlpha(color: string, alpha: number): string {
  const hex = expandShortHex(color);
  if (!HEX_COLOR_PATTERN.test(hex)) {
    return color;
  }
  const value = parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 0xff}, ${(value >> 8) & 0xff}, ${value & 0xff}, ${alpha})`;
}

/**
 * WCAG relative luminance (0 = black, 1 = white) for a `#rgb` or `#rrggbb` color.
 *
 * Shorthand has to be expanded before parsing: `#fcc` read as-is is 0x000fcc, a dark blue rather
 * than a pale red, which inverts any threshold or contrast check built on the result. The plan
 * node palette writes several of its colors that way.
 */
export function hexRelativeLuminance(color: string): number {
  const value = parseInt(expandShortHex(color).slice(1), 16);
  const [r, g, b] = [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff].map((channel) => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Below this luminance a dot reads as a hole rather than a color once it sits on the dark-mode
// badge fill. Tuned against `getNiceColors()`, whose darkest entries (e.g. #172622) are otherwise
// indistinguishable from the surface.
const DARK_MODE_MIN_DOT_LUMINANCE = 0.25;
const DARK_MODE_LIFT_STEP = 0.18;
const DARK_MODE_MAX_LIFT_STEPS = 8;

/**
 * Lift an accent color that is too dark to read against a dark-mode surface.
 *
 * Colors already above the threshold are returned untouched, so most of the palette stays
 * identical between themes and only the few unreadable entries move.
 */
export function lightenForDarkMode(color: string): string {
  if (!HEX_COLOR_PATTERN.test(color)) {
    return color;
  }
  let lifted = color;
  // Lift in steps rather than one jump so a nearly-bright color keeps its hue instead of washing
  // out to near-white; the step cap bounds the loop for any input that cannot reach the threshold.
  for (
    let step = 0;
    step < DARK_MODE_MAX_LIFT_STEPS && hexRelativeLuminance(lifted) < DARK_MODE_MIN_DOT_LUMINANCE;
    step++
  ) {
    lifted = shadeHexColor(lifted, DARK_MODE_LIFT_STEP);
  }
  return lifted;
}

/**
 * Above this luminance a fill cannot reach 3:1 (WCAG 1.4.11, non-text contrast) against a white
 * surface, which is what a few-pixel swatch needs to register at all. Set as high as that bar
 * allows, so a swatch stays as close as it can to the pale card it stands for; the palette's worst
 * case lands at 3.18:1 on the surface and 3.01:1 under the minimap's dim mask.
 */
const LIGHT_MODE_MAX_SWATCH_LUMINANCE = 0.28;
/**
 * Saturation floor and ceiling for a chromatic swatch: pale tints carry too little chroma to keep
 * their hue once deepened, and a fully saturated result reads as an alert rather than a category.
 */
const LIGHT_MODE_MIN_SATURATION = 0.42;
const LIGHT_MODE_MAX_SATURATION = 0.72;
/**
 * Below this saturation a color is a near-grey whose hue is rounding noise. Amplifying it invents a
 * color that was never in the palette (`#f4f7f6` becomes teal), so these stay neutral. The plan
 * palette splits cleanly either side of this: its greys sit under 0.16 and its tints over 0.64.
 */
const LIGHT_MODE_NEUTRAL_SATURATION = 0.25;
// Fine steps so a color stops just under the ceiling rather than overshooting into a needlessly
// deep shade; the step cap bounds the loop for any input that cannot reach the target.
const LIGHT_MODE_DARKEN_STEP = 0.02;
const LIGHT_MODE_MAX_DARKEN_STEPS = 60;

/**
 * Deepen a pale fill that is too light to read as a small swatch on a light surface.
 *
 * Hue is preserved and lightness alone is reduced, so a shrunken plan node keeps the color identity
 * of the card it stands for. That is why this cannot be `shadeHexColor` toward black: mixing a
 * near-white tint with black yields grey long before it reaches the luminance target.
 *
 * Colors already dark enough are returned untouched, so a palette that never had a contrast problem
 * stays byte-identical across themes.
 */
export function darkenForLightMode(color: string): string {
  const hex = expandShortHex(color);
  if (
    !HEX_COLOR_PATTERN.test(hex) ||
    hexRelativeLuminance(hex) <= LIGHT_MODE_MAX_SWATCH_LUMINANCE
  ) {
    return color;
  }

  const [hue, saturation, lightness] = hexToHsl(hex);
  const targetSaturation =
    saturation < LIGHT_MODE_NEUTRAL_SATURATION
      ? 0
      : Math.min(LIGHT_MODE_MAX_SATURATION, Math.max(saturation, LIGHT_MODE_MIN_SATURATION));

  // Step down rather than solving for lightness directly: luminance is a non-linear function of
  // HSL lightness.
  let currentLightness = lightness;
  let darkened = hslToHex(hue, targetSaturation, currentLightness);
  for (
    let step = 0;
    step < LIGHT_MODE_MAX_DARKEN_STEPS &&
    hexRelativeLuminance(darkened) > LIGHT_MODE_MAX_SWATCH_LUMINANCE;
    step++
  ) {
    currentLightness = Math.max(0, currentLightness - LIGHT_MODE_DARKEN_STEP);
    darkened = hslToHex(hue, targetSaturation, currentLightness);
  }
  return darkened;
}

/** `#rrggbb` to HSL, each component in 0..1. */
function hexToHsl(hex: string): [number, number, number] {
  const value = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff].map((c) => c / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) {
    return [0, 0, lightness];
  }
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  const hue =
    max === r
      ? ((g - b) / delta + (g < b ? 6 : 0)) / 6
      : max === g
        ? ((b - r) / delta + 2) / 6
        : ((r - g) / delta + 4) / 6;
  return [hue, saturation, lightness];
}

/** HSL (each component 0..1) back to `#rrggbb`. */
function hslToHex(hue: number, saturation: number, lightness: number): string {
  const chroma = saturation * Math.min(lightness, 1 - lightness);
  const channel = (n: number) => {
    const k = (n + hue * 12) % 12;
    const value = lightness - chroma * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(Math.max(0, Math.min(1, value)) * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`;
}
